import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthConfig, useMe, usePlatformStatus, useSwitchTenant, useTenants } from "../../api/hooks";
import { useSession } from "../../app/store";
import { errText } from "../../components/ui";
import { CredentialsDialog } from "./CredentialsDialog";
import "./platform.css";

/** Header badge: who you are, which operator (tenant) you act for, switch tenant, logout, vault. */
export function TenantMenu() {
  const me = useMe();
  const tenants = useTenants();
  const auth = useAuthConfig();
  const status = usePlatformStatus(!!me.data);
  const sw = useSwitchTenant();
  const toast = useSession((s) => s.toast);
  const setProject = useSession((s) => s.setProject);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [vault, setVault] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const user = me.data;
  const active = tenants.data?.active ?? user?.tenant ?? "";
  const activeName = tenants.data?.options?.find((o) => o.id === active)?.name ?? active;
  // Second header line: the operator's display name when known; otherwise the role rather than
  // a bare UUID (the NEXTnet session only carries operator ids).
  const primaryRole = (user?.roles ?? []).find((r) => /admin/i.test(r)) ?? user?.roles?.[0];
  const subtitle = activeName && activeName !== active ? activeName : (primaryRole ?? (user?.mode === "dev" ? "dev" : ""));

  const switchTo = async (tenant: string) => {
    if (tenant === active) return;
    try {
      await sw.mutateAsync(tenant);
      setProject(null);
      setOpen(false);
      toast(`acting for ${tenants.data?.options?.find((o) => o.id === tenant)?.name ?? tenant}`, "success");
      navigate("/flights");
    } catch (e) {
      toast(errText(e), "error");
    }
  };

  const dbBad = status.data && !status.data.database.ok;
  const redisBad = status.data?.redis.configured && status.data.redis.ok === false;

  return (
    <div className="tenant-menu" ref={ref}>
      <button className={`btn ghost sm tenant-btn${open ? " on" : ""}`} onClick={() => setOpen(!open)} title={`tenant ${active}`}>
        <span className="tenant-avatar">{(user?.name ?? "?").slice(0, 1).toUpperCase()}</span>
        <span className="tenant-label">
          <span>{user?.name ?? "…"}</span>
          <span className="faint small">{subtitle}</span>
        </span>
        {dbBad || redisBad ? <span className="status-dot failed" title="a platform service is unreachable" /> : null}
        <span className="faint">▾</span>
      </button>
      {open ? (
        <div className="menu">
          <div className="menu-head">
            <div>{user?.name}</div>
            <div className="small dim">{user?.email ?? user?.id}</div>
            <div className="row wrap" style={{ gap: 4, marginTop: 4 }}>
              {(user?.roles ?? []).map((r) => (
                <span key={r} className={`badge${/admin/i.test(r) ? " info" : ""}`}>
                  {r}
                </span>
              ))}
              <span className="badge">{user?.mode === "nextnet" ? "NEXTnet session" : "dev auth"}</span>
            </div>
          </div>
          <div className="menu-section">Operator</div>
          {(tenants.data?.options ?? [{ id: active, name: activeName }]).map((o) => (
            <button key={o.id} className={`menu-item${o.id === active ? " on" : ""}`} onClick={() => switchTo(o.id)} disabled={sw.isPending || (!tenants.data?.can_switch && o.id !== active)}>
              <span className={`status-dot${o.id === active ? " done" : ""}`} />
              <span>{o.name}</span>
              {o.name !== o.id ? <span className="faint small mono">{o.id}</span> : null}
            </button>
          ))}
          {user?.is_admin ? <TenantAdder onAdd={switchTo} /> : null}
          <div className="menu-section">Platform</div>
          <button className="menu-item" onClick={() => (setVault(true), setOpen(false))}>
            🔑 Credentials vault
          </button>
          <button className="menu-item" onClick={() => (navigate("/admin"), setOpen(false))}>
            🛡 Audit log &amp; status
          </button>
          {status.data ? (
            <div className="menu-status small dim">
              <span>
                db <StatusWord ok={status.data.database.ok} /> {status.data.database.dialect}
                {status.data.database.rls ? " · RLS" : ""}
              </span>
              <span>
                jobs {status.data.job_backend}
                {status.data.redis.configured ? (
                  <>
                    {" "}
                    · redis <StatusWord ok={!!status.data.redis.ok} />
                  </>
                ) : null}
              </span>
              <span>blob {status.data.blob.kind}</span>
            </div>
          ) : null}
          {auth.data?.mode === "nextnet" ? (
            <>
              <div className="menu-section" />
              <a className="menu-item" href={auth.data.logout_url}>
                ⎋ Sign out
              </a>
            </>
          ) : null}
        </div>
      ) : null}
      {vault ? <CredentialsDialog onClose={() => setVault(false)} /> : null}
    </div>
  );
}

function StatusWord({ ok }: { ok: boolean }) {
  return <span className={ok ? "sev-ok" : "sev-error"}>{ok ? "ok" : "down"}</span>;
}

function TenantAdder({ onAdd }: { onAdd: (t: string) => void }) {
  const [v, setV] = useState("");
  return (
    <div className="row" style={{ padding: "4px 10px", gap: 4 }}>
      <input
        className="inp sm mono"
        style={{ flex: 1 }}
        placeholder="operator id…"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && v.trim()) onAdd(v.trim().toLowerCase());
        }}
      />
      <button className="btn sm" disabled={!v.trim()} onClick={() => onAdd(v.trim().toLowerCase())} title="administrators may act for any operator">
        Act as
      </button>
    </div>
  );
}
